import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { 
  ArrowRight, 
  ArrowLeft, 
  Scale,
  Frame,
  Heart,
  Lightbulb,
  Info,
  Brain,
  CheckCircle2,
  Link2
} from "lucide-react";
import StepWrapper from "./StepWrapper";
import { cn } from "@/lib/utils";

const chainLinks = [
  { 
    key: "helpfulFrame", 
    label: "Helpful Frame", 
    description: "Have I clearly defined the decision situation?",
    helpText: "A helpful frame defines the specific decision, who's involved, timing, and boundaries. Ask yourself: Am I solving the right problem?",
    icon: Frame,
    color: "chain-frame",
    bgColor: "bg-chain-frame/10",
    textColor: "text-chain-frame"
  },
  { 
    key: "clearValues", 
    label: "Clear Values", 
    description: "Do I know what I want and don't want?",
    helpText: "Clear values identify what matters most to you. Have you articulated your wants, needs, and what you're trying to avoid?",
    icon: Heart,
    color: "chain-values",
    bgColor: "bg-chain-values/10",
    textColor: "text-chain-values"
  },
  { 
    key: "creativeAlternatives", 
    label: "Creative Alternatives", 
    description: "Have I explored different options?",
    helpText: "Creative alternatives go beyond obvious choices. Have you brainstormed freely without constraints? Combined options? Asked others for ideas?",
    icon: Lightbulb,
    color: "chain-alternatives",
    bgColor: "bg-chain-alternatives/10",
    textColor: "text-chain-alternatives"
  },
  { 
    key: "usefulInformation", 
    label: "Useful Information", 
    description: "Do I have the information I need?",
    helpText: "Useful information helps you understand consequences and probabilities. Do you know enough to confidently predict outcomes?",
    icon: Info,
    color: "chain-information",
    bgColor: "bg-chain-information/10",
    textColor: "text-chain-information"
  },
  { 
    key: "soundReasoning", 
    label: "Sound Reasoning", 
    description: "Have I thought through the logic?",
    helpText: "Sound reasoning connects your values to your choice. Have you weighed trade-offs logically without letting emotions or biases mislead you?",
    icon: Brain,
    color: "chain-reasoning",
    bgColor: "bg-chain-reasoning/10",
    textColor: "text-chain-reasoning"
  },
  { 
    key: "commitmentToFollow", 
    label: "Commitment to Follow Through", 
    description: "Am I ready to act on this decision?",
    helpText: "Commitment means you're prepared to act and stick with it. Have you considered what might derail you and how to stay on track?",
    icon: CheckCircle2,
    color: "chain-commitment",
    bgColor: "bg-chain-commitment/10",
    textColor: "text-chain-commitment"
  },
];

const getRatingLabel = (value) => {
  if (value === 0) return "Not considered";
  if (value < 30) return "Just started";
  if (value < 50) return "Need more work";
  if (value < 70) return "Making progress";
  if (value < 90) return "Almost there";
  return "Confident";
};

const getRatingColor = (value) => {
  if (value < 30) return "text-destructive";
  if (value < 50) return "text-warning";
  if (value < 70) return "text-accent-warm";
  return "text-success";
};

const StepEvaluate = ({ data, updateRating, onNext, onBack, coachMode = true }) => {
  const { ratings } = data;
  const weakestLink = Math.min(...Object.values(ratings));
  const averageRating = Object.values(ratings).reduce((a, b) => a + b, 0) / 6;

  return (
    <StepWrapper
      stepNumber={4}
      title="Evaluate Your Decision"
      subtitle={coachMode ? "Rate the strength of each link in your decision chain." : null}
    >
      <div className="space-y-6">
        {/* Introduction Context - only in coach mode */}
        {coachMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-primary/5 border border-primary/20 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Link2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-1">The Decision Chain Concept</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Think of your decision as a chain with six links. The chain breaks at its weakest point — 
                  so even if five links are strong, one weak link can undermine everything. 
                  This step helps you honestly assess where you are and where to improve.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Scale Guide - only in coach mode */}
        {coachMode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-4 bg-secondary/50 border-border">
              <div className="flex items-start gap-3">
                <Scale className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-foreground mb-1">Rating Scale</p>
                  <div className="grid grid-cols-3 gap-4 text-muted-foreground">
                    <div><span className="font-medium text-destructive">0%</span> — Haven&apos;t considered</div>
                    <div><span className="font-medium text-warning">50%</span> — Needs more work</div>
                    <div><span className="font-medium text-success">100%</span> — Confident & ready</div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Chain Links */}
        <div className="space-y-4">
          {chainLinks.map((link, index) => {
            const value = ratings[link.key];
            const Icon = link.icon;
            
            return (
              <motion.div
                key={link.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + index * 0.05 }}
              >
                <Card className={cn(
                  "p-5 bg-card border-border shadow-sm transition-all duration-300",
                  value >= 70 && "ring-1 ring-success/30"
                )}>
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                      link.bgColor
                    )}>
                      <Icon className={cn("w-5 h-5", link.textColor)} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-foreground">{link.label}</h3>
                        <div className="flex items-center gap-2">
                          <span className={cn("text-sm font-medium", getRatingColor(value))}>
                            {getRatingLabel(value)}
                          </span>
                          <span className="text-lg font-bold text-foreground">
                            {value}%
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {link.description}
                      </p>
                      {coachMode && link.helpText && (
                        <p className="text-xs text-muted-foreground/80 mb-3 italic">
                          {link.helpText}
                        </p>
                      )}
                      
                      <Slider
                        value={[value]}
                        onValueChange={([newValue]) => updateRating(link.key, newValue)}
                        max={100}
                        step={5}
                        className={cn(
                          "w-full",
                          `[&_[role=slider]]:border-${link.color}`,
                        )}
                      />
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className={cn(
            "p-6 bg-card border-border shadow-card",
            weakestLink < 50 && "border-warning/50",
            weakestLink >= 70 && "border-success/50"
          )}>
            <h3 className="font-semibold text-foreground mb-4">Decision Chain Strength</h3>
            
            {/* Visual chain */}
            <div className="flex items-center gap-1 mb-4">
              {chainLinks.map((link) => {
                const value = ratings[link.key];
                return (
                  <div key={link.key} className="flex-1">
                    <div className="h-3 rounded-full bg-muted overflow-hidden">
                      <motion.div
                        className={cn(
                          "h-full rounded-full",
                          `bg-${link.color}`
                        )}
                        initial={{ width: 0 }}
                        animate={{ width: `${value}%` }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-muted rounded-xl">
                <p className="text-sm text-muted-foreground">Weakest Link</p>
                <p className={cn("text-2xl font-bold", getRatingColor(weakestLink))}>
                  {weakestLink}%
                </p>
              </div>
              <div className="p-3 bg-muted rounded-xl">
                <p className="text-sm text-muted-foreground">Average</p>
                <p className="text-2xl font-bold text-foreground">
                  {Math.round(averageRating)}%
                </p>
              </div>
            </div>
            
            {weakestLink < 50 && (
              <p className="mt-4 text-sm text-warning bg-warning/10 p-3 rounded-lg">
                💡 Focus on improving your weakest links before making a final decision.
              </p>
            )}
          </Card>
        </motion.div>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex justify-between items-center pt-4"
        >
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </StepWrapper>
  );
};

export default StepEvaluate;
